import type {
  ProjectReportPeriod,
  ProjectTaskReport,
  ProjectTaskReportQuery,
  ProjectTaskReportSection,
} from '../types/projectReport';
import { calendarDaysBetween, parseCalendarDateInput } from './dateTime';

/** Labels only — the API turns each period into dates from the current date. */
export const PROJECT_REPORT_PERIOD_OPTIONS: ReadonlyArray<{
  value: ProjectReportPeriod;
  label: string;
}> = [
  { value: 'TODAY', label: 'Today' },
  { value: 'YESTERDAY', label: 'Yesterday' },
  { value: 'THIS_WEEK', label: 'This Week' },
  { value: 'LAST_WEEK', label: 'Last Week' },
  { value: 'THIS_MONTH', label: 'This Month' },
  { value: 'LAST_MONTH', label: 'Last Month' },
  { value: 'CUSTOM', label: 'Custom Date Range' },
];

export const DEFAULT_PROJECT_REPORT_PERIOD: ProjectReportPeriod = 'THIS_WEEK';

/** Mirrors the API limit so the user gets feedback before a round trip. */
export const PROJECT_REPORT_MAX_RANGE_DAYS = 366;

export function projectReportPeriodLabel(period: ProjectReportPeriod): string {
  return (
    PROJECT_REPORT_PERIOD_OPTIONS.find(option => option.value === period)
      ?.label ?? period
  );
}

/** Returns an error message, or null when the custom range is usable. */
export function validateProjectReportRange(
  fromDate: string,
  toDate: string,
): string | null {
  if (!fromDate.trim() || !toDate.trim()) return 'Select a From Date and To Date.';
  if (!parseCalendarDateInput(fromDate) || !parseCalendarDateInput(toDate)) {
    return 'Enter valid dates.';
  }
  const days = calendarDaysBetween(fromDate, toDate);
  if (days === null || days < 0) return 'To Date must be on or after From Date.';
  if (days + 1 > PROJECT_REPORT_MAX_RANGE_DAYS) {
    return `The date range cannot exceed ${PROJECT_REPORT_MAX_RANGE_DAYS} days.`;
  }
  return null;
}

/**
 * Query params shared by the View (JSON) and Download (PDF / CSV / XLSX)
 * calls. The project id travels in the URL path.
 */
export function projectTaskReportParams(
  query: ProjectTaskReportQuery,
): Record<string, string | number> {
  const params: Record<string, string | number> = { period: query.period };
  if (query.period === 'CUSTOM') {
    if (query.fromDate) params.fromDate = query.fromDate;
    if (query.toDate) params.toDate = query.toDate;
  }
  if (query.employeeId) params.employeeId = query.employeeId;
  return params;
}

/** Employee-wise sections in API order, each with its own subtotal. */
export function groupProjectReportByEmployee(
  report: ProjectTaskReport,
): ProjectTaskReportSection[] {
  const sections = new Map<number, ProjectTaskReportSection>();
  for (const summary of report.employees) {
    sections.set(summary.employeeId, { ...summary, rows: [] });
  }
  for (const row of report.rows) {
    let section = sections.get(row.employeeId);
    if (!section) {
      section = {
        employeeId: row.employeeId,
        employeeNumber: row.employeeNumber,
        employeeName: row.employeeName,
        taskCount: 0,
        completedCount: 0,
        spentMinutesInRange: 0,
        rows: [],
      };
      sections.set(row.employeeId, section);
    }
    section.rows.push(row);
  }
  return [...sections.values()].filter(section => section.rows.length > 0);
}
